import { useIntl } from 'react-intl';
import classNames from 'classnames';

import { LinkButton } from '@components/LinkButton';

import { recipeMessages } from '@data/ui-messages/recipes';
import { CmsRecipePageDocument } from '@interfaces/common';

import styles from './RecipeDetails.module.css';

type RecipeDetailsProps = Pick<
	CmsRecipePageDocument,
	'categories' | 'cuisine' | 'ingredients' | 'instructions'
>;

export const RecipeDetails: React.VFC<RecipeDetailsProps> = ({
	categories = [],
	cuisine,
	ingredients,
	instructions,
}) => {
	const intl = useIntl();
	const tags = cuisine ? [cuisine, ...categories] : categories;

	return (
		<section
			className={classNames(styles['recipe-details'], 'grid')}
			data-testid="RecipeDetails"
		>
			<div className="stack-block">
				<h2 className="fluid-font-t4-bold">
					{intl.formatMessage(recipeMessages.ingredients)}
				</h2>

				<ul className="stack-block">
					{ingredients.map(({ quantity, title, _key }) => (
						<li
							className={classNames(styles.ingredient, 'grid', 'fluid-font-t5')}
							key={_key}
						>
							<span className="fluid-font-t5-bold">{quantity}</span> {title}
						</li>
					))}
				</ul>
			</div>

			<div className={classNames(styles.method, 'stack-block')}>
				<h2 className="fluid-font-t4-bold">
					{intl.formatMessage(recipeMessages.method)}
				</h2>

				<ol
					aria-label={intl.formatMessage(recipeMessages.instructionListLabel)}
					className={styles.instructions}
				>
					{instructions.map(({ text, _key }, index) => (
						<li className={classNames(styles.step)} key={_key}>
							<div className={styles['step-marker']}>
								<span
									className={classNames(
										styles['step-number'],
										'fluid-font-t5-bold'
									)}
								>
									{index + 1}
								</span>

								<span className={styles['step-line']} />
							</div>

							<p className={classNames(styles.instruction, 'fluid-font-t5')}>
								{text}
							</p>
						</li>
					))}
				</ol>

				<hr className={styles.break} />

				<ul
					aria-label={intl.formatMessage(recipeMessages.tagList)}
					className={styles.categories}
				>
					{tags?.map(({ _type, title, slug }) => (
						<li key={slug}>
							<LinkButton
								className={classNames(
									styles['category-link'],
									'fluid-font-t6-bold'
								)}
								text={title}
								url={{
									pathname: '/recipes/filtered',
									query: {
										[_type === 'recipeCategory' ? 'categories' : 'cuisine']:
											slug,
									},
								}}
							/>
						</li>
					))}
				</ul>
			</div>
		</section>
	);
};
